import { useCallback, useRef, useState, type ReactNode } from 'react';
import { CameraScanField } from './CameraScanField';
import { useModalDialog } from './useModalDialog';

export interface PromptRequest {
  /** Names the dialog to a screen reader as well as heading it, the way every other modal here does. */
  title: string;
  label: string;
  defaultValue?: string;
  confirmLabel?: string;
  /** Every caller so far refuses a blank answer, so the confirm stays shut until something is typed. */
  allowEmpty?: boolean;
  /** The server's own limit, so a long answer is cut while typing rather than refused after confirming. */
  maxLength?: number;
  /**
   * The answer is a code off a label (a shelf): on a phone a camera button sits beside the box. A read only fills
   * the box; the person still confirms, because the answer commits a write [SSD-7527].
   */
  scan?: boolean;
}

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  /** Names the way out when the action is itself a cancel, or when the pair reads better as Yes/No than as OK/Cancel. */
  cancelLabel?: string;
}

export interface ChoiceRequest extends ConfirmRequest {
  secondaryLabel: string;
}

type Pending =
  | { kind: 'choice'; request: ChoiceRequest; settle: (answer: 'primary' | 'secondary' | null) => void }
  | { kind: 'prompt'; request: PromptRequest; settle: (answer: string | null) => void }
  | { kind: 'confirm'; request: ConfirmRequest; settle: (agreed: boolean) => void };

interface PromptModalProps {
  request: PromptRequest;
  onSubmit: (answer: string) => void;
  onCancel: () => void;
}

const PromptModal = ({ request, onSubmit, onCancel }: PromptModalProps) => {
  const [value, setValue] = useState(request.defaultValue ?? '');
  const dialogRef = useModalDialog(onCancel);

  const answer = value.trim();
  const blocked = !request.allowEmpty && answer === '';
  const box = (
    <input
      type="text"
      aria-label={request.label}
      maxLength={request.maxLength}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      // Enter submits, because the prompt this replaced did and the desk types one-handed.
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !blocked) {
          onSubmit(answer);
        }
      }}
    />
  );

  return (
    <dialog className="wms-prompt" ref={dialogRef} aria-label={request.title}>
      <header>
        {request.title}
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">
        <div className="qrow">
          <b>{request.label}</b>
          {request.scan ? (
            <CameraScanField label={`Scan ${request.label.toLowerCase()} with camera`} ultimate enterAfterRead={false}>
              {box}
            </CameraScanField>
          ) : (
            box
          )}
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={blocked} onClick={() => onSubmit(answer)}>
          {request.confirmLabel ?? 'OK'}
        </button>
      </div>
    </dialog>
  );
};

interface ConfirmModalProps {
  request: ConfirmRequest;
  onAgree: () => void;
  onCancel: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

const ConfirmModal = ({ request, onAgree, onCancel, secondaryLabel, onSecondary }: ConfirmModalProps) => {
  const dialogRef = useModalDialog(onCancel);

  return (
    <dialog className="wms-prompt" ref={dialogRef} aria-label={request.title}>
      <header>
        {request.title}
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">{request.message}</div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          {request.cancelLabel ?? 'Cancel'}
        </button>
        {secondaryLabel && (
          <button type="button" className="btn btn-line btn-sm" onClick={onSecondary}>
            {secondaryLabel}
          </button>
        )}
        <button type="button" className="btn btn-red-line btn-sm" onClick={onAgree}>
          {request.confirmLabel ?? 'OK'}
        </button>
      </div>
    </dialog>
  );
};

const answerBackedOut = (question: Pending) => {
  if (question.kind === 'confirm') {
    question.settle(false);
  } else {
    question.settle(null);
  }
};

/**
 * The in-page replacement for `window.prompt` and `window.confirm`. It stays promise-shaped so a call
 * site reads the way the imperative one did — ask, then act on the answer — instead of splintering into
 * an open handler and a confirm handler for every one of a screen's dozen questions.
 *
 * The answer comes back trimmed, and `null`/`false` mean the operator backed out. A caller then simply
 * stops — no write and no notification: the dialog closing is the answer, and a red "Cancelled" toast
 * read as a failure of something the operator had chosen not to do.
 */
export const usePromptDialog = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const openQuestion = useRef<Pending | null>(null);

  /** One dialog shows at a time, so a newer question ends the one before it as if backed out rather than leaving its caller waiting. */
  const show = useCallback((next: Pending | null) => {
    const replaced = openQuestion.current;
    openQuestion.current = next;
    setPending(next);
    if (next !== null && replaced !== null) {
      answerBackedOut(replaced);
    }
  }, []);

  /** Closes whatever is open as if backed out, for a page whose question no longer applies. */
  const dismissQuestion = useCallback(() => {
    const open = openQuestion.current;
    if (open !== null) {
      openQuestion.current = null;
      setPending(null);
      answerBackedOut(open);
    }
  }, []);

  const ask = useCallback(
    (request: PromptRequest) => new Promise<string | null>((settle) => show({ kind: 'prompt', request, settle })),
    [show],
  );

  const askConfirm = useCallback(
    (request: ConfirmRequest) => new Promise<boolean>((settle) => show({ kind: 'confirm', request, settle })),
    [show],
  );

  const askChoice = useCallback(
    (request: ChoiceRequest) =>
      new Promise<'primary' | 'secondary' | null>((settle) => show({ kind: 'choice', request, settle })),
    [show],
  );

  let dialog: ReactNode = null;
  if (pending?.kind === 'prompt') {
    const { request, settle } = pending;
    dialog = (
      <PromptModal
        request={request}
        onSubmit={(answer) => {
          show(null);
          settle(answer);
        }}
        onCancel={() => {
          show(null);
          settle(null);
        }}
      />
    );
  } else if (pending?.kind === 'choice') {
    const { request, settle } = pending;
    const finish = (answer: 'primary' | 'secondary' | null) => {
      show(null);
      settle(answer);
    };
    dialog = (
      <ConfirmModal
        request={request}
        onAgree={() => finish('primary')}
        secondaryLabel={request.secondaryLabel}
        onSecondary={() => finish('secondary')}
        onCancel={() => finish(null)}
      />
    );
  } else if (pending?.kind === 'confirm') {
    const { request, settle } = pending;
    dialog = (
      <ConfirmModal
        request={request}
        onAgree={() => {
          show(null);
          settle(true);
        }}
        onCancel={() => {
          show(null);
          settle(false);
        }}
      />
    );
  }

  return { ask, askConfirm, askChoice, dialog, questionOpen: pending !== null, dismissQuestion };
};
